import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { documentTotals, type DocumentLine } from '@finvox/shared/invoice-math';
import { parseMoneyInput, roundMoney } from '@finvox/shared/money';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { Select } from '@/components/ui/Select';
import { useOrg } from '@/contexts/OrgContext';
import { usePaginatedPartySelect } from '@/hooks/usePaginatedPartySelect';
import { Pagination } from '@/components/ui/Pagination';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

function PaymentForm({
  type,
  partyId,
  documentId
}: {
  type: 'ar' | 'ap';
  partyId: string;
  documentId: string;
}) {
  const { t } = useTranslation();
  const { orgId, currentPeriodId } = useOrg();
  const online = useOnlineStatus();
  const [amount, setAmount] = useState('0.00');

  async function pay() {
    if (!orgId || !currentPeriodId) return;
    await apiPost('/v1/payments/record', {
      orgId,
      type,
      partyId,
      amount: roundMoney(amount),
      date: new Date().toISOString().slice(0, 10),
      periodId: currentPeriodId,
      documentId
    });
  }

  return (
    <div className="mt-6 rounded border border-[var(--fv-border)] p-4">
      <h3 className="font-medium">{t('sales.recordPayment')}</h3>
      <input
        className="mt-2 rounded border border-[var(--fv-border)] px-3 py-2"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <Button className="mt-2" onClick={() => void pay()} disabled={!online}>
        {t('sales.recordPayment')}
      </Button>
    </div>
  );
}

export function SalesInvoicePage() {
  const { t } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { orgId, skipLiveUpdate } = useOrg();
  const online = useOnlineStatus();
  const customers = usePaginatedPartySelect(orgId, 'customers');
  const [invoice, setInvoice] = useState<Record<string, unknown> | null>(null);
  const [lines, setLines] = useState<DocumentLine[]>([]);

  useEffect(() => {
    if (!orgId || !invoiceId) return;
    return onSnapshot(
      doc(getFirestoreDb(), 'organizations', orgId, 'salesInvoices', invoiceId),
      (snap) => {
        if (skipLiveUpdate(snap.metadata, orgId)) return;
        const d = snap.data();
        if (!d) return;
        setInvoice(d);
        setLines((d.lines ?? []) as DocumentLine[]);
      }
    );
  }, [orgId, invoiceId, skipLiveUpdate]);

  const totals = useMemo(() => documentTotals(lines), [lines]);
  const readonly = invoice?.status === 'posted';

  async function save(patch: Record<string, unknown>) {
    if (!orgId || !invoiceId || !online || readonly) return;
    await updateDoc(doc(getFirestoreDb(), 'organizations', orgId, 'salesInvoices', invoiceId), {
      ...patch,
      updatedAt: serverTimestamp()
    });
  }

  async function post() {
    if (!orgId || !invoiceId) return;
    await apiPost('/v1/sales-invoices/post', { orgId, invoiceId });
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('sales.invoice')}</h1>
      {readonly && <p className="text-sm text-[var(--fv-text-muted)]">{t('journals.postedReadonly')}</p>}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="text-sm">
          {t('sales.customer')}
          <Select
            className="mt-1 w-full"
            disabled={readonly}
            value={String(invoice?.customerId ?? '')}
            onValueChange={(id) => void save({ customerId: id })}
            options={customers.parties.map((c) => ({
              value: c.id,
              label: `${c.code} — ${c.name}`
            }))}
          />
          <Pagination
            className="mt-2 border-0 px-0"
            pageIndex={customers.pageIndex}
            hasNext={customers.hasNext}
            hasPrev={customers.hasPrev}
            onNext={customers.nextPage}
            onPrev={customers.prevPage}
            loading={customers.loading}
          />
        </label>
        <label className="text-sm">
          {t('journals.date')}
          <DatePicker
            className="mt-1"
            disabled={readonly}
            value={String(invoice?.date ?? '')}
            onChange={(d) => void save({ date: d })}
          />
        </label>
      </div>
      <table className="mt-4 w-full text-sm">
        <thead>
          <tr>
            <th className="p-2">{t('sales.qty')}</th>
            <th className="p-2">{t('sales.unitPrice')}</th>
            <th className="p-2">{t('sales.taxPercent')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => (
            <tr key={i} className="border-t border-[var(--fv-border)]">
              <td className="p-2">
                <input
                  type="number"
                  className="w-20 rounded border px-2 py-1"
                  disabled={readonly}
                  value={line.quantity}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, quantity: Number(e.target.value) || 1 };
                    setLines(next);
                    void save({ lines: next });
                  }}
                />
              </td>
              <td className="p-2">
                <input
                  className="w-28 rounded border px-2 py-1"
                  disabled={readonly}
                  value={line.unitPrice}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, unitPrice: parseMoneyInput(e.target.value) };
                    setLines(next);
                    void save({ lines: next });
                  }}
                />
              </td>
              <td className="p-2">
                <input
                  type="number"
                  className="w-16 rounded border px-2 py-1"
                  disabled={readonly}
                  value={line.taxRatePercent}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, taxRatePercent: Number(e.target.value) || 0 };
                    setLines(next);
                    void save({ lines: next });
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-4 font-semibold">
        {t('sales.total')}: <MoneyDisplay value={totals.gross} className="font-semibold" />
      </p>
      {!readonly && (
        <Button className="mt-4" onClick={() => void post()} disabled={!online}>
          {t('sales.postInvoice')}
        </Button>
      )}
      {readonly && (
        <div className="mt-6 space-y-3 rounded border border-[var(--fv-border)] p-4 text-sm">
          {Boolean(invoice.documentNumber) && (
            <p>
              <span className="text-[var(--fv-text-muted)]">{t('sales.documentNumber')}: </span>
              {String(invoice.documentNumber)}
            </p>
          )}
          {Boolean(invoice.eInvoiceUuid) && (
            <p className="font-mono text-xs">{String(invoice.eInvoiceUuid)}</p>
          )}
          {(() => {
            const payload = invoice.eInvoicePayload as { qrTlv?: string } | undefined;
            const qr = payload?.qrTlv;
            if (!qr) return null;
            const img = `https://quickchart.io/qr?text=${encodeURIComponent(qr)}&size=160`;
            return (
              <img src={img} alt={t('settings.eInvoiceQr')} className="rounded border bg-white p-2" width={160} />
            );
          })()}
          {Boolean(invoice.eInvoiceUuid) && orgId && invoiceId && (
            <Button
              variant="secondary"
              disabled={!online}
              onClick={() => {
                void apiPost<{ payload: Record<string, unknown> }>('/v1/e-invoice/export', {
                  orgId,
                  invoiceId
                }).then((res) => {
                  const blob = new Blob([JSON.stringify(res.payload, null, 2)], {
                    type: 'application/json'
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `e-invoice-${String(invoice.documentNumber ?? invoiceId)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                });
              }}
            >
              {t('sales.exportEInvoice')}
            </Button>
          )}
        </div>
      )}
      {invoice?.status === 'posted' && (
        <PaymentForm type="ar" partyId={String(invoice.customerId)} documentId={invoiceId!} />
      )}
    </div>
  );
}
